import restClient from "api/restClient";
import { graphqlPost } from "api/graphqlClient";
import { Button } from "components/Button/Button";
import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import RecordTable from "components/Table/RecordTable";
import { getInitialQueryWithTableSettings, TableColumn } from "components/Table/tableUtils";
import workspaceConfig from "config/resources/workspace";
import { getUserPermissionData } from "containers/Auth/Auth.selectors";
import { hasUserUpdateWorkspacePermission } from "containers/Auth/permissions";
import { logScreenId } from "containers/BundleManager/BundleManager";
import { RecordLogLevel } from "graphql/generated/graphql";
import { History } from "history";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getTypedLowerCase } from "utils/service";
import { useIsVisible } from "utils/useIsVisible";
import { getSearchField, parseFilterSafely } from "utils/utils";
import style from "./LogsListing.module.scss";
import {
    gradualPageSize,
    initialQuery,
    logsTableBaseOptions,
    LOGS_MAX_IN_MEMORY_ROWS_DEFAULT,
    LOGS_MAX_IN_MEMORY_ROWS_KEY,
} from "./constants";
import { clearLogsMutation } from "./fetchRecordLogList";
import {
    gradualFooterState,
    loadNewerCancelledLabel,
    loadNewerProgressLabel,
    loadOlderCancelledLabel,
    newerLogsAboveLabel,
    shouldAutoPrefetchOlder,
} from "./gradualLog";
import { useDelayedTrue } from "./useDelayedTrue";
import { useGradualRecordLogs } from "./useGradualRecordLogs";
import { useStickyTopOffset } from "./useStickyTopOffset";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";

// Suppress a brief "Showing N so far, scanning older…" flash when the backend
// answers quickly: the scanning footer appears only once a scan has been in flight
// longer than this grace period. A fast load (the common case, especially the
// early prefetch) completes within it, so the user never sees the message
// (Stefan, 2026-10-14, nice-to-have). The error and end-of-logs footers are never
// delayed — only the transient scanning state is.
const SCANNING_FOOTER_GRACE_MS = 400;

// Format the oldest instant the gradual scan has reached for the "scanned back
// to …" hint. Shown in the user's local timezone (the raw value is a UTC ISO
// instant); falls back to the raw value if it is not parseable.
const formatScannedThrough = (iso: string | null): string | null => {
    if (!iso) {
        return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return iso;
    }
    return date.toLocaleString();
};

interface LogsListingProps {
    resourceName: string;
    id: string;
    /** The single record to scope logs to, passed as the gradual endpoint's
     *  `record_id` ARGUMENT (not just a filter clause) so the server derives the
     *  created_at floor from that record and the walk terminates cleanly at the
     *  record's creation instead of running to the retention horizon. Set ONLY by
     *  the single-record Logs tab (RecordLogList). Left undefined for container /
     *  workspace logs, whose project/bundle semijoin must cover CHILD records'
     *  logs — scoping those to `record_id = <container>` would drop the children.
     *  See operator-task-fe-logs-tab-gradual-load-day-by-day-no-pagination. */
    recordScopeId?: string;
    /** The container (project / bundle / other container record) whose logs the
     *  filter's semijoin selects. Passed as the gradual endpoint's `container_id`
     *  argument so the server floors the walk at the earliest creation among the
     *  container and its members (the scan ends there instead of at the retention
     *  horizon). Set ONLY by ContainerLogList for a non-workspace container. */
    containerScopeId?: string;
    additionalQueryField?: { [key: string]: any };
    queryInfo: {
        limit: number;
        offset: number;
        order: string;
    };
    columns: TableColumn[];
    updateQuery: (newQuery: any) => void;
    idList?: string[];
    history: History;
    queryParams: { [key: string]: string };
    isLiveUpdatesEnabled: boolean;
    tableSettingsKey?: string;
}

export const LogsListing = ({
    resourceName,
    id,
    recordScopeId,
    containerScopeId,
    additionalQueryField,
    queryInfo,
    columns,
    idList,
    queryParams,
    isLiveUpdatesEnabled,
    tableSettingsKey,
}: LogsListingProps) => {
    const permissionData = useSelector(getUserPermissionData);
    const canUserUpdateWorkspace = useMemo(
        () => hasUserUpdateWorkspacePermission(permissionData),
        [permissionData]
    );

    const isWorkspace = resourceName === workspaceConfig.resourceName;

    const buildFilterStr = useCallback(
        (includeAdditional: boolean) => {
            const filters: Array<any> = [];
            if (queryParams.filter) {
                const filterOptions = parseFilterSafely(queryParams.filter);
                const filtersMappedOptions = (filterOptions.and || []).map((field) => {
                    if (field.field === "recordLogType") {
                        return {
                            field: "record_id--record.id:record_type",
                            op: "in",
                            value: field.value.map((value) =>
                                value.toUpperCase().replaceAll(" ", "_")
                            ),
                        };
                    } else if (field.field === "recordLogName") {
                        return getSearchField({
                            term: field.value,
                            field: "record_id--record.id:name",
                            idFieldName: "record_id--record.id:id",
                        });
                    } else if (field.field === "subjectOrData") {
                        const subjectField = getSearchField({
                            term: field.value,
                            field: "subject",
                        });
                        const dataField = getSearchField({ term: field.value, field: "data" });
                        return { or: [subjectField, dataField] };
                    }
                    return field;
                })
                    // Drop `in` clauses whose value list is empty. An empty
                    // multiselect (e.g. no record types selected) means "no
                    // constraint", NOT "match nothing" — forwarding it as
                    // `record_type IN ()` matches zero rows and renders a
                    // permanently-empty Logs tab. This surfaced as the Logs tab
                    // showing "no matching data" whenever a stale
                    // `recordLogType in []` filter was persisted for a record
                    // (operator-task-fe-operator-identity-execution-log-read-gap).
                    .filter(
                        (clause) =>
                            !(
                                clause &&
                                clause.op === "in" &&
                                Array.isArray(clause.value) &&
                                clause.value.length === 0
                            )
                    );
                filters.push(...filtersMappedOptions);
            }
            if (includeAdditional && additionalQueryField && !isEmpty(additionalQueryField)) {
                filters.push(...Object.values(additionalQueryField));
            }
            return filters.length > 0 ? JSON.stringify({ and: filters }) : null;
        },
        [queryParams.filter, additionalQueryField]
    );

    const filter = useMemo(() => buildFilterStr(true), [buildFilterStr]);

    // Partition-pruned gradual read, replacing the former unbounded record_log
    // subscription (which full-scanned every retained daily partition twice —
    // rows + an eager COUNT — on every open and every live push). Row scoping is
    // in the filter (a record_id clause for a single record, the container
    // semijoin, or none for the workspace) exactly as the subscription built it.
    // For the single-record Logs tab, `recordScopeId` ALSO passes that record as
    // the gradual endpoint's `record_id` argument, from which the server derives a
    // created_at floor so the walk terminates at the record's creation rather than
    // scanning empty partitions back to the retention horizon; container/workspace
    // logs leave it undefined (their semijoin must cover child records' logs).
    // `enabled` follows the live-updates toggle: on (default) it loads and
    // re-scans on filter / page-size change; off freezes the current view. v1
    // does not re-push on every record_log notify (flagged to founders on the
    // task); "Load older" walks further back through history. See
    // operator-task-fe-record-logs-tab-default-load-slow-no-partition-bound.
    // Scroll-to-top-gated live subscription (Stefan 2026-10-12, items 2 & 3): a
    // zero-footprint sentinel at the TOP of the list. While it is in view the user
    // is looking at the newest rows, so the live row subscription streams; once
    // they scroll away from the top it leaves the viewport and the subscription is
    // torn down (new rows only ever land at the top, so there is nothing to stream
    // while scrolled into history, and re-subscribing on return pulls the newest
    // window so nothing is missed). Observed against the viewport (default root) so
    // it works whichever ancestor actually scrolls. Only gates the live
    // subscription — older-log loading keeps working when scrolled down.
    // See operator-task-fe-logs-tab-bounded-dom-virtualization (leg D).
    const topSentinelRef = useRef<HTMLDivElement>(null);
    const atTop = useIsVisible(topSentinelRef);

    // Bound how many log rows are kept in browser memory (Stefan, 2026-10-15):
    // live updates + gradual older-loading otherwise accumulate rows unbounded,
    // which also shrinks the virtual-list scrollbar handle to an unusable sliver.
    // Configurable via the LOGS_MAX_IN_MEMORY_ROWS User-Interface workspace config;
    // fall back to the default when the key is unset / non-positive.
    const maxInMemoryConfig = useWorkspaceConfigValue(LOGS_MAX_IN_MEMORY_ROWS_KEY);
    const maxInMemoryRows =
        typeof maxInMemoryConfig === "number" && maxInMemoryConfig > 0
            ? maxInMemoryConfig
            : LOGS_MAX_IN_MEMORY_ROWS_DEFAULT;

    const {
        rows,
        isScanning,
        isLoadingNewer,
        isNewerPaused,
        newestEvictedEpoch,
        newerPrependEpoch,
        newerAddedCount,
        oldestLoadedEpoch,
        moreAvailable,
        scannedThrough,
        error,
        loadOlder,
        cancel,
        reload,
        cancelNewer,
        resumeNewer,
        goToNewest,
        goToOldest,
    } = useGradualRecordLogs({
            recordId: recordScopeId,
            containerId: containerScopeId,
            filter,
            // Load a full screenful per gesture (initial + each "load older"),
            // not the legacy pagination default of 10, which barely advanced the
            // endless-scroll list (Stefan, 2026-10-13). A persisted larger
            // rows-per-page still wins if set. See `gradualPageSize`.
            pageSize: Math.max(queryInfo.limit || 0, gradualPageSize),
            enabled: isLiveUpdatesEnabled,
            liveSubscribe: atTop,
            // Discard the oldest rows beyond this cap while at the top; they
            // re-load contiguously on scroll-down via the reset older-cursor.
            maxRows: maxInMemoryRows,
        });

    // Keep the user's place while newer rows are prepended above it during history
    // browsing (Stefan, 2026-10-15: "the list should be extended upwards only
    // without loosing the current scroll position"; 2026-11-02: option (a)). The
    // logs view opts out of native scroll anchoring (see LogsListing.module.scss),
    // so compensate by hand: when the hook reports a prepend (newerPrependEpoch),
    // grow scrollTop by the height the list grew. Live pushes at the top and the
    // catch-up on a return to the top while not browsing keep the newest rows in
    // view instead.
    // The reverse case: deep in history, the hook drops the newest rows from the
    // top once the in-memory cap is reached (newestEvictedEpoch bumps); shrink
    // scrollTop by the height the list lost so the row being read stays put.
    const logsScreenRef = useRef<HTMLDivElement>(null);
    const prependAnchorRef = useRef<{
        height: number;
        evictedEpoch: number;
        prependEpoch: number;
        oldestEpoch: number;
    } | null>(null);
    const getScroller = useCallback(
        () => logsScreenRef.current?.closest(".main__content") as HTMLElement | null,
        []
    );
    // Pin the sticky top bars below the record screen's own sticky header
    // (breadcrumbs, title, buttons, tabs), else they sit hidden behind it.
    const stickyTop = useStickyTopOffset(getScroller);
    useLayoutEffect(() => {
        const scroller = getScroller();
        const previous = prependAnchorRef.current;
        if (scroller && previous && oldestLoadedEpoch !== previous.oldestEpoch) {
            // "Go to oldest" landed: show the end of the list.
            scroller.scrollTop = scroller.scrollHeight;
        } else if (scroller && previous && newerPrependEpoch !== previous.prependEpoch) {
            const grownBy = scroller.scrollHeight - previous.height;
            if (grownBy > 0) {
                scroller.scrollTop += grownBy;
            }
        } else if (scroller && previous && newestEvictedEpoch !== previous.evictedEpoch) {
            const shrunkBy = previous.height - scroller.scrollHeight;
            if (shrunkBy > 0) {
                scroller.scrollTop = Math.max(0, scroller.scrollTop - shrunkBy);
            }
        }
        prependAnchorRef.current = {
            height: scroller?.scrollHeight ?? 0,
            evictedEpoch: newestEvictedEpoch,
            prependEpoch: newerPrependEpoch,
            oldestEpoch: oldestLoadedEpoch,
        };
    }, [rows, newestEvictedEpoch, newerPrependEpoch, oldestLoadedEpoch, getScroller]);
    // Scroll up only as far as the log table's header row, which then sits at
    // the top of the visible area just below the record's sticky header
    // (Stefan, 2026-11-03): not to the top of the record screen. Never scrolls
    // down.
    const scrollToLogHeader = useCallback(() => {
        const scroller = getScroller();
        if (!scroller) {
            return;
        }
        const anchor =
            logsScreenRef.current?.querySelector("thead") ?? logsScreenRef.current ?? null;
        if (!anchor) {
            scroller.scrollTop = 0;
            return;
        }
        const anchorTop =
            anchor.getBoundingClientRect().top -
            scroller.getBoundingClientRect().top +
            scroller.scrollTop -
            stickyTop;
        scroller.scrollTop = Math.max(0, Math.min(scroller.scrollTop, anchorTop));
    }, [getScroller, stickyTop]);
    // The jump removes the "Go to newest" bar above the table (and may reload
    // the newest rows), which moves the header up after the click. Re-align
    // after each commit until the newest rows have loaded.
    const pendingHeaderScrollRef = useRef(false);
    useLayoutEffect(() => {
        if (!pendingHeaderScrollRef.current) {
            return;
        }
        scrollToLogHeader();
        if (!isLoadingNewer) {
            pendingHeaderScrollRef.current = false;
        }
    });
    const handleGoToNewest = useCallback(() => {
        goToNewest();
        pendingHeaderScrollRef.current = true;
        scrollToLogHeader();
    }, [goToNewest, scrollToLogHeader]);
    const loadingNewerVisible = useDelayedTrue(isLoadingNewer, SCANNING_FOOTER_GRACE_MS);
    const showNewerHeader = (loadingNewerVisible && isLoadingNewer) || isNewerPaused;

    const hasRows = rows.length > 0;
    const showJumpBars = hasRows && isLiveUpdatesEnabled;
    // Full-table spinner only on the very first window; a background "load older"
    // keeps the already-loaded rows visible and shows progress in the footer.
    const isInitialLoading = isScanning && !hasRows;
    const footer = gradualFooterState({
        isScanning,
        moreAvailable,
        hasError: Boolean(error),
        rowCount: rows.length,
        scannedThrough,
    });

    // Scroll-driven older-log loading (all Logs-tab views). Stefan, 2026-10-10:
    // drop the "Load older logs" button; load older logs EARLY while scrolling so
    // the user never reaches the end while more logs exist, and when they do reach
    // a still-scanning end just show the spinner + how-far-back progress (already
    // in the footer) — "when the button would be visible it is already scanning".
    // A sentinel below the list, observed with a generous bottom rootMargin, trips
    // BEFORE the real bottom and drives loadOlder(). Stefan, 2026-10-13: this now
    // applies to the shared container/workspace logs page too ("it should
    // automatically load older logs when scrolled to the very bottom"). Safe
    // despite that page having no created_at floor, because each gesture is ONE
    // bounded count-mode fetch: a screenful loads, fills the viewport and pushes
    // the sentinel out of view, so the walk advances one user-driven step at a
    // time rather than running to the retention horizon.
    // See operator-task-fe-logs-tab-gradual-load-day-by-day-no-pagination and
    // operator-task-fe-logs-tab-bounded-dom-virtualization.
    const prefetchSentinelRef = useRef<HTMLDivElement>(null);
    // Root the prefetch IntersectionObserver on the actual scroll container
    // (.main__content, the page scroller) rather than the default window viewport.
    // With the default viewport root the 600px bottom rootMargin cannot trip early:
    // the sentinel is clipped by the inner scroller, so it only intersects once it
    // is essentially AT the bottom (verified on develop 2026-10-14 — prefetch fired
    // at the bottom, not ~600px before). Rooting on the scroller makes "600px before
    // the scroller's visible bottom" the real trigger line, so older logs start
    // loading slightly before the user reaches the end (Stefan, 2026-10-14
    // nice-to-have). Falls back to the viewport root if the scroller is not found,
    // preserving the previous behaviour.
    const [scrollRoot, setScrollRoot] = useState<Element | null>(null);
    useEffect(() => {
        setScrollRoot(prefetchSentinelRef.current?.closest(".main__content") ?? null);
    }, [hasRows]);
    const sentinelVisible = useIsVisible(prefetchSentinelRef, {
        root: scrollRoot,
        rootMargin: "600px 0px",
    });
    // When the user cancels an in-flight "load older" at the very bottom, the
    // scanning footer's Cancel just stops the fetch (keeping moreAvailable true);
    // but the prefetch sentinel is still in view there, so the level-triggered
    // auto-prefetch below would immediately re-fire loadOlder() and the fetch could
    // never actually be cancelled — the page thrashes (Stefan, 2026-10-15: "scroll
    // to the very bottom and press cancel → the page becomes unresponsive"). Latch
    // auto-prefetch off on cancel; resume either by pressing Resume (the Cancel
    // button turns into a Resume button — Stefan, 2026-10-15: "instead of having to
    // scroll up & down again … the cancel button switches to a resume button") or
    // by scrolling away from the end (sentinel out of view) and back. State (not a
    // ref) so the footer can render the Resume affordance while paused; the
    // auto-prefetch effect lists it as a dep so pressing Resume re-fires a load.
    const [isLoadOlderPaused, setIsLoadOlderPaused] = useState(false);
    const handleCancel = useCallback(() => {
        setIsLoadOlderPaused(true);
        cancel();
    }, [cancel]);
    const handleResume = useCallback(() => {
        // Clearing the latch lets the auto-prefetch effect (which depends on it)
        // re-fire loadOlder() immediately while the sentinel is still at the bottom.
        setIsLoadOlderPaused(false);
    }, []);
    // Resume eligibility once the user scrolls away from the end (sentinel out of
    // view); scrolling back down then re-arms a single load.
    useEffect(() => {
        if (!sentinelVisible && isLoadOlderPaused) {
            setIsLoadOlderPaused(false);
        }
    }, [sentinelVisible, isLoadOlderPaused]);
    useEffect(() => {
        if (isLoadOlderPaused) {
            return;
        }
        if (
            shouldAutoPrefetchOlder({
                enabled: isLiveUpdatesEnabled,
                sentinelVisible,
                moreAvailable,
                isScanning,
                hasRows,
            })
        ) {
            loadOlder();
        }
    }, [
        isLoadOlderPaused,
        isLiveUpdatesEnabled,
        sentinelVisible,
        moreAvailable,
        isScanning,
        hasRows,
        loadOlder,
    ]);

    // Grace-gate the scanning footer so a fast scan does not flash the
    // "scanning older…" message (Stefan, 2026-10-14): it shows only once a scan has
    // been in flight past SCANNING_FOOTER_GRACE_MS, and a quick load resolves first
    // and never renders it.
    const scanningGraceElapsed = useDelayedTrue(isScanning, SCANNING_FOOTER_GRACE_MS);

    // The "load-older" affordance is the scroll sentinel (no button) on every
    // Logs-tab view now, so suppress the otherwise-empty "Load older logs" footer
    // state. The end-of-logs / error footer states show immediately; the scanning
    // footer waits out the grace period above.
    const scanningVisible =
        footer.kind === "scanning" && scanningGraceElapsed && !isLoadOlderPaused;
    // After Cancel, auto-prefetch is latched off and the scan has stopped, but
    // older history remains — surface a Resume affordance instead of silently
    // requiring a scroll gesture (Stefan, 2026-10-15). Not while an error is shown
    // (that takes priority with its own Retry) or mid-scan.
    const pausedVisible =
        isLoadOlderPaused &&
        !isScanning &&
        moreAvailable &&
        hasRows &&
        footer.kind !== "error";
    const showFooter =
        footer.kind === "error" ||
        footer.kind === "end-of-logs" ||
        scanningVisible ||
        pausedVisible;

    const [isClearingLogs, setIsClearingLogs] = useState(false);
    const [isDownloadingLogs, setIsDownloadingLogs] = useState(false);

    const downloadLogs = async () => {
        setIsDownloadingLogs(true);
        try {
            const params: Record<string, string> = { order: "+created_at" };
            if (!isWorkspace) {
                params.record_id = id;
            }
            const filterStr = buildFilterStr(isWorkspace);
            if (filterStr) {
                params.filter = filterStr;
            }
            const response = await restClient.get("/record_log/export", {
                params,
                responseType: "blob",
            });
            const contentDisposition = response.headers["content-disposition"] || "";
            const match = contentDisposition.match(/filename="([^"]+)"/);
            const fileName = match ? match[1] : "logs.log";
            const url = URL.createObjectURL(new Blob([response.data], { type: "text/plain" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error: any) {
            NotificationService.error(<RestErrorNotification error={error} />);
        } finally {
            setIsDownloadingLogs(false);
        }
    };

    const clearLogs = () => {
        setIsClearingLogs(true);

        if (isWorkspace) {
            restClient
                .delete(`/record_log`)
                .catch((error) =>
                    NotificationService.error(<RestErrorNotification error={error} />)
                )
                .finally(() => setIsClearingLogs(false));
        } else {
            const promises = (idList ?? [id])?.map((id) =>
                graphqlPost({
                    query: clearLogsMutation(
                        resourceName === "bundles" ? "bundle_repository" : resourceName
                    ),
                    variables: { id },
                })
                    .catch((error) =>
                        NotificationService.error(<RestErrorNotification error={error} />)
                    )
            );
            Promise.allSettled(promises).finally(() => setIsClearingLogs(false));
        }
    };
    const button = (callback) => (
        <Button
            onClick={callback}
            label="Delete all logs"
            loading={isClearingLogs}
            variation="dark"
            disabled={isInitialLoading || !hasRows || !isLiveUpdatesEnabled}
        />
    );

    const showWarning = () =>
        showConfirmModal({
            content: (
                <div className="warningContent">
                    This will remove <span style={{ fontWeight: 700 }}>all</span> logs. Are you
                    sure?
                </div>
            ),
            onConfirm: clearLogs,
            onCancel: () => {},
            cancelButtonProps: cancelButtonProps,
            confirmButtonProps: { className: "button button--primary-blue" },
            className: "confirmationModal",
            centered: true,
        });

    const clearLogsButton = !isWorkspace
        ? button(showWarning)
        : canUserUpdateWorkspace && button(showWarning);

    const downloadLogsButton = (
        <Button
            onClick={downloadLogs}
            label="Download logs"
            loading={isDownloadingLogs}
            variation="dark"
            disabled={!hasRows || isDownloadingLogs}
        />
    );

    return (
        <div id={logScreenId} className={style.logsScreen} ref={logsScreenRef}>
            <div className={style.clearLogsButton}>
                {downloadLogsButton}
                {clearLogsButton}
            </div>
            {/* Top sentinel: marks the newest-rows top of the list. While it is in
                view the live subscription streams; scrolling away tears it down
                (leg D — scroll-to-top-gated live subscription). Zero footprint. */}
            <div ref={topSentinelRef} aria-hidden className={style.topSentinel} />
            {/* Top header for the forward catch-up: progress + Cancel while newer
                logs load, or a Resume button once cancelled. Sticky so it stays in
                view while the user's place is kept below the prepended rows. */}
            {showNewerHeader && (
                <div className={style.gradualHeader} style={{ top: stickyTop }}>
                    {isNewerPaused ? (
                        <>
                            <span className={style.gradualProgress}>
                                {loadNewerCancelledLabel(rows.length)}
                            </span>
                            <Button
                                onClick={resumeNewer}
                                label="Load newer logs"
                                variation="dark"
                            />
                        </>
                    ) : (
                        <>
                            <span className={style.gradualProgress}>
                                {loadNewerProgressLabel(rows.length)}
                            </span>
                            <Button onClick={cancelNewer} label="Cancel" variation="dark" />
                        </>
                    )}
                </div>
            )}
            {/* Scrolled away from the newest rows: say how many newer logs were
                loaded above the user's place and offer a jump to the newest rows,
                which also resumes live updates (Stefan, 2026-11-02). */}
            {!showNewerHeader && showJumpBars && !atTop && (
                <div className={style.gradualHeader} style={{ top: stickyTop }}>
                    {newerAddedCount > 0 && (
                        <span className={style.gradualProgress}>
                            {newerLogsAboveLabel(newerAddedCount)}
                        </span>
                    )}
                    <Button
                        onClick={handleGoToNewest}
                        label="Go to newest"
                        variation="dark"
                        size="small"
                    />
                </div>
            )}
            <RecordTable
                data={rows}
                // No pagination: the accumulated gradual rows render as ONE
                // single list (Stefan's ask #2 — drop the page-size, "n–m of x"
                // and jump-to-page chrome entirely). Omitting `updateQuery` keeps
                // RecordTable client-side; with `pagination: false` it renders
                // every loaded row rather than slicing a page. The exact
                // cross-partition total is deliberately not computed (that COUNT
                // is the second full-partition scan this feature avoids — Stefan,
                // 2026-09-25); each "load older" gesture extends the list by one
                // more count-mode screenful fetched via the continuation cursor.
                totalItemCount={rows.length}
                columns={columns}
                isLoading={isInitialLoading}
                recordTypes={null}
                options={{
                    // pagination off, sticky horizontal scrollbar on, windowed
                    // body: see logsTableBaseOptions in ./constants.
                    ...logsTableBaseOptions,
                    setRowProps: (row, dataIndex, rowIndex) => {
                        const currenRowData = rows[dataIndex];
                        const className = getTypedLowerCase(
                            currenRowData?.level?.toLowerCase() as RecordLogLevel
                        );
                        return { className };
                    },
                }}
                initialQuery={
                    tableSettingsKey
                        ? getInitialQueryWithTableSettings(tableSettingsKey, initialQuery)
                        : initialQuery
                }
                tableSettingsKey={tableSettingsKey}
                className={style.logsListingTable}
                isDisabled={!isLiveUpdatesEnabled}
            />
            {/* Prefetch sentinel: placed below the list and observed with a
                bottom rootMargin so older logs start loading BEFORE the user
                reaches the real end (seamless scroll), on every Logs-tab view.
                See the shouldAutoPrefetchOlder effect above. */}
            <div ref={prefetchSentinelRef} aria-hidden className={style.prefetchSentinel} />
            {/* Jump to the oldest logs: the bottom then shows the end of the logs
                and scrolling up loads newer logs (Stefan, 2026-11-02). */}
            {showJumpBars && moreAvailable && (
                <div className={style.jumpBottomBar}>
                    <Button
                        onClick={goToOldest}
                        label="Go to oldest"
                        variation="dark"
                        size="small"
                    />
                </div>
            )}
            {showFooter && (
                <div className={style.gradualFooter}>
                    {pausedVisible && (
                        <>
                            <span className={style.gradualProgress}>
                                {loadOlderCancelledLabel(
                                    rows.length,
                                    formatScannedThrough(scannedThrough)
                                )}
                            </span>
                            <Button
                                onClick={handleResume}
                                label="Scan for older logs"
                                variation="dark"
                            />
                        </>
                    )}
                    {!pausedVisible && footer.kind === "scanning" && (
                        <>
                            <span className={style.gradualProgress}>
                                {footer.label}
                                {formatScannedThrough(footer.scannedThrough)
                                    ? ` (scanned back to ${formatScannedThrough(
                                          footer.scannedThrough
                                      )})`
                                    : ""}
                            </span>
                            <Button onClick={handleCancel} label="Cancel" variation="dark" />
                        </>
                    )}
                    {footer.kind === "end-of-logs" && (
                        <span className={style.gradualProgress}>
                            Beginning of logs — no older logs exist.
                        </span>
                    )}
                    {footer.kind === "error" && (
                        <>
                            <span className={style.gradualError}>
                                Could not load logs
                                {error?.message ? `: ${error.message}` : ""}
                            </span>
                            <Button onClick={reload} label="Retry" variation="dark" />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
